import { PartialSanityImageType } from "@silversea-ssc/ui-components-react";
import { isPresent } from "src/utils/checks";
import {
  MainMenuItemFragment,
  MainMenuGroupFragment,
  LocationSpecificMainMenuItemFragment,
  TopMenuItemFragment
} from "types/graphql-types";

export type ValidatedMainMenuItem =
  | (Omit<MainMenuItemFragment, "page"> & {
      page: MainMenuItemFragment["page"] & {
        fullPath: string;
      };
    })
  | (Omit<LocationSpecificMainMenuItemFragment, "page"> & {
      page: LocationSpecificMainMenuItemFragment["page"] & {
        fullPath: string;
      };
    });

export type ValidatedPanelSection = Omit<MainMenuGroupFragment, "items"> & {
  items: ValidatedMainMenuItem[];
};

export type ValidatedTopMenuItemSpecialType =
  | "rab"
  | "raq"
  | "sfo"
  | "my-silversea";
export type ValidatedTopMenuItem = {
  _key: string;
  title: string;
  href: string;
  openNewTab: boolean;
  specialType?: ValidatedTopMenuItemSpecialType;
  trackingName?: string;
};

export const toValidatedMainMenuGroup = (
  item: MainMenuGroupFragment
): ValidatedPanelSection => {
  const validItems = (item.items || []).filter(
    (x): x is ValidatedMainMenuItem =>
      isPresent(x) && isPresent(x.page?.fullPath)
  );
  return { ...item, items: validItems };
};

export const pageTitleOrOverride = (item: ValidatedMainMenuItem): string => {
  return item.title?.localized || item.page?.card?.title?.localized || "";
};

export const pageDescriptionOrOverride = (
  item: ValidatedMainMenuItem
): string => {
  return (
    item.content?.localized || item.page?.card?.description?.localized || ""
  );
};

export type MenuItemImage =
  | PartialSanityImageType
  | {
      _type: "svg";
      url: string;
    }
  | { _type: "icon"; name: string }
  | { _type: "package-icon"; name: string };

export const toMenuItemImage = (
  image: PartialSanityImageType | undefined,
  icon: string | undefined
): MenuItemImage | undefined => {
  if (icon) return { _type: "icon", name: icon };
  if (image) return image;

  return undefined;
};

export const toValidatedTopMenuItems = (
  items: (TopMenuItemFragment | undefined)[]
): ValidatedTopMenuItem[] =>
  items.map(toValidatedTopMenuItem).filter(isPresent);

const toValidatedTopMenuItem = (
  item: TopMenuItemFragment | undefined
): ValidatedTopMenuItem | undefined => {
  if (!item) return undefined;
  const href =
    item.linkType == "page"
      ? item.page?.fullPath
      : item.linkType == "link"
        ? item.link?.localized
        : undefined;

  if (!href) return undefined;

  return {
    _key: item._key || "",
    title: item.title?.localized || "",
    href: href,
    openNewTab: item.openNewTab || false,
    specialType:
      (item.specialType as ValidatedTopMenuItemSpecialType) || undefined,
    trackingName: item.trackingName
  };
};
